const STORAGE_KEY = 'br:wishlist:v1';

function readWishlist() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item) => item && item.id && item.handle) : [];
  } catch {
    return [];
  }
}

function writeWishlist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    return false;
  }

  document.dispatchEvent(new CustomEvent('br:wishlist:change', { detail: { items } }));
  return true;
}

function updateWishlistLinks(items = readWishlist()) {
  document.querySelectorAll('[data-br-wishlist-count]').forEach((badge) => {
    badge.textContent = String(items.length);
    badge.toggleAttribute('hidden', items.length === 0);
  });
}

class BrWishlistButton extends HTMLElement {
  #button;
  #status;

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#status = this.querySelector('[data-br-wishlist-status]');
    this.#button?.addEventListener('click', this.#toggle);
    document.addEventListener('br:wishlist:change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#toggle);
    document.removeEventListener('br:wishlist:change', this.#sync);
  }

  #getProduct() {
    return {
      id: String(this.dataset.productId || ''),
      handle: this.dataset.productHandle || '',
      title: this.dataset.productTitle || '',
      url: this.dataset.productUrl || '',
      image: this.dataset.productImage || '',
      price: this.dataset.productPrice || '',
      comparePrice: this.dataset.productComparePrice || '',
      productType: this.dataset.productType || '',
      variantId: String(this.dataset.variantId || ''),
      variantAvailable: this.dataset.variantAvailable !== 'false',
      savedAt: Date.now(),
    };
  }

  #sync = () => {
    const saved = readWishlist().some((item) => String(item.id) === String(this.dataset.productId));
    const label = this.querySelector('.br-wishlist-button__label');
    const text = saved ? label?.dataset.removeLabel : label?.dataset.addLabel;

    this.#button?.setAttribute('aria-pressed', String(saved));
    this.#button?.setAttribute('aria-label', text || '');
    if (label && text) label.textContent = text;
  };

  #toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const product = this.#getProduct();
    const items = readWishlist();

    const index = items.findIndex(
      (item) =>
        String(item.id) === product.id
    );

    const removing = index >= 0;

    if (removing) {
      items.splice(index, 1);
    } else {
      items.unshift(product);
    }

    if (!writeWishlist(items)) {
      if (this.#status) {
        this.#status.textContent =
          this.dataset.errorStatus || '';
      }

      return;
    }

    if (this.#status) {
      this.#status.textContent = removing
        ? this.dataset.removedStatus || ''
        : this.dataset.savedStatus || '';
    }
  };
}

class BrWishlistPage extends HTMLElement {
  #cardCache = new Map();
  #pendingCards = new Map();
  #activeRequests = 0;

  connectedCallback() {
    document.addEventListener('br:wishlist:change', this.#render);
    this.addEventListener('click', this.#handleClick);
    this.#render();
    this.#importSharedWishlist();
  }

  disconnectedCallback() {
    document.removeEventListener('br:wishlist:change', this.#render);
    this.removeEventListener('click', this.#handleClick);
    this.#pendingCards.clear();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const shareButton = event.target.closest('[data-br-wishlist-share]');
    if (shareButton) {
      this.#shareWishlist();
      return;
    }

    const removeButton = event.target.closest('[data-br-wishlist-remove]');
    if (!removeButton) return;

    const productId = String(removeButton.dataset.brWishlistRemove || '');
    const buttons = [...this.querySelectorAll('[data-br-wishlist-remove]')];
    const index = buttons.indexOf(removeButton);
    const restoreFocus = document.activeElement === removeButton;
    if (writeWishlist(readWishlist().filter((item) => String(item.id) !== productId)) && restoreFocus) {
      const remaining = this.querySelectorAll('[data-br-wishlist-remove]');
      (remaining[Math.min(index, remaining.length - 1)] || this.querySelector('.br-wishlist__shop'))?.focus();
    }
  };

  #render = () => {
    const items = readWishlist();
    const grid = this.querySelector('[data-br-wishlist-grid]');
    const empty = this.querySelector('[data-br-wishlist-empty]');
    const share = this.querySelector('[data-br-wishlist-share]');
    const count = this.querySelector('[data-br-wishlist-count-label]');

    if (!grid || !empty) return;

    empty.toggleAttribute('hidden', items.length > 0);
    grid.toggleAttribute('hidden', items.length === 0);
    share?.toggleAttribute('hidden', items.length === 0);

    if (count) {
      const countLabel = items.length === 1 ? this.dataset.itemLabel : this.dataset.itemsLabel;
      count.textContent = `${items.length} ${countLabel || ''}`.trim();
      count.toggleAttribute('hidden', items.length === 0);
    }

    // Keep loaded cards (and their controls) mounted when a favorite is removed.
    const existing = new Map([...grid.children].map((card) => [card.dataset.productId, card]));
    const wanted = new Map(items.map((item) => [String(item.id), item.handle]));
    existing.forEach((card, id) => {
      if (wanted.get(id) !== card.dataset.productHandle) {
        card.remove();
        existing.delete(id);
        this.#pendingCards.delete(id);
      }
    });

    items.forEach((item, index) => {
      const id = String(item.id);
      let card = existing.get(id);
      if (!card) {
        card = this.#createSkeleton(item);
        this.#pendingCards.set(id, { item, placeholder: card });
      } else if (card.classList.contains('br-wishlist__skeleton') && !card.dataset.loading) {
        this.#pendingCards.set(id, { item, placeholder: card });
      }
      if (grid.children[index] !== card) grid.insertBefore(card, grid.children[index] || null);
    });

    this.#syncLoadingState();
    this.#loadCards();
  };

  #syncLoadingState() {
    const grid = this.querySelector('[data-br-wishlist-grid]');
    if (!grid || !this.isConnected) return;
    const loading = Boolean(grid.querySelector('.br-wishlist__skeleton'));
    const wasLoading = grid.getAttribute('aria-busy') === 'true';
    grid.toggleAttribute('aria-busy', loading);
    if (loading) grid.setAttribute('aria-busy', 'true');
    this.querySelector('[data-br-wishlist-error]')?.toggleAttribute(
      'hidden', !grid.querySelector('.br-wishlist-card-fallback'),
    );
    if (loading && !wasLoading) this.#announce(this.dataset.loadingLabel || 'Loading your saved favorites…');
    if (!loading && wasLoading) this.#announce(this.querySelector('[data-br-wishlist-count-label]')?.textContent || '');
  }

  async #shareWishlist() {
    const items = readWishlist();
    const handles = items.map((item) => item.handle).filter(Boolean);
    if (handles.length === 0) return;

    const url = new URL(this.dataset.sharePath || window.location.pathname, window.location.origin);
    url.searchParams.set('wishlist', handles.join(','));

    const shareData = {
      title: this.dataset.shareTitle || document.title,
      text: this.dataset.shareTitle || document.title,
      url: url.toString(),
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(shareData.url);
      this.#announce(this.dataset.shareCopiedLabel || 'Wishlist link copied');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#announce(this.dataset.addError || 'Wishlist link could not be copied.');
    }
  }

  async #importSharedWishlist() {
    const url = new URL(window.location.href);
    const rawHandles = url.searchParams.get('wishlist');
    if (!rawHandles) return;

    const handles = [...new Set(rawHandles.split(','))]
      .map((handle) => handle.trim())
      .filter((handle) => /^[a-z0-9][a-z0-9-]*$/i.test(handle))
      .slice(0, 50);

    if (handles.length === 0) return;

    const imported = (
      await Promise.all(handles.map((handle) => this.#fetchProduct(handle)))
    ).filter(Boolean);

    if (imported.length === 0) return;

    const merged = [...imported, ...readWishlist()].filter(
      (item, index, list) =>
        list.findIndex((candidate) => String(candidate.id) === String(item.id)) === index,
    );

    writeWishlist(merged);
    url.searchParams.delete('wishlist');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  async #fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return null;
      const product = await response.json();
      const variant = product.variants?.find((candidate) => candidate.available) || product.variants?.[0];
      if (!variant) return null;

      const image = variant.featured_image?.src || product.featured_image || product.images?.[0] || '';

      return {
        id: String(product.id || ''),
        handle: product.handle || handle,
        title: product.title || '',
        url: product.url || `/products/${encodeURIComponent(handle)}`,
        image,
        price: this.#formatMoney(variant.price),
        comparePrice:
          variant.compare_at_price > variant.price
            ? this.#formatMoney(variant.compare_at_price)
            : '',
        productType: product.type || '',
        variantId: String(variant.id || ''),
        variantAvailable: Boolean(variant.available),
        savedAt: Date.now(),
      };
    } catch {
      return null;
    }
  }

  #formatMoney(value) {
    const amount = Number(value) / 100;
    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  #announce(message) {
    const status = this.querySelector('[data-br-wishlist-page-status]');
    if (status) status.textContent = message;
  }

  async #fetchCardMarkup(item) {
    if (this.#cardCache.has(item.handle)) {
      return this.#cardCache.get(item.handle);
    }

    const request = this.#requestCardMarkup(item);
    this.#cardCache.set(item.handle, request);

    try {
      return await request;
    } catch (error) {
      this.#cardCache.delete(item.handle);
      throw error;
    }
  }

  #loadCards() {
    if (!this.isConnected) return;
    // One queue per page keeps the four-request limit even during rapid updates.
    while (this.#activeRequests < 4 && this.#pendingCards.size) {
      const [id, { item, placeholder }] = this.#pendingCards.entries().next().value;
      this.#pendingCards.delete(id);
      if (!placeholder.isConnected) continue;
      placeholder.dataset.loading = 'true';
      this.#activeRequests += 1;
      this.#fetchCardMarkup(item)
        .then((markup) => this.#showCard(placeholder, this.#createServerCard(markup, item), item))
        .catch(() => this.#showCard(placeholder, this.#createFallbackCard(item), item))
        .finally(() => {
          delete placeholder.dataset.loading;
          this.#activeRequests -= 1;
          this.#syncLoadingState();
          this.#loadCards();
        });
    }
  }

  #showCard(placeholder, card, item) {
    const grid = this.querySelector('[data-br-wishlist-grid]');
    if (!this.isConnected || placeholder.parentElement !== grid) return;
    card.dataset.productHandle = item.handle;
    card.classList.add('br-wishlist__item--enter');

    const image = card.querySelector('.product-media__image');
    if (image) {
      if ([...grid.children].indexOf(placeholder) < 4) image.loading = 'eager';
      if (!image.complete) {
        card.classList.add('br-wishlist__item--media-loading');
        const reveal = () => {
          card.classList.remove('br-wishlist__item--media-loading');
          image.removeEventListener('load', reveal);
          image.removeEventListener('error', reveal);
        };
        image.addEventListener('load', reveal);
        image.addEventListener('error', reveal);
      }
    }
    placeholder.replaceWith(card);
  }

  async #requestCardMarkup(item) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(
      `${root}products/${encodeURIComponent(item.handle)}`,
      window.location.origin,
    );
    url.searchParams.set('section_id', 'br-wishlist-card-fragment');

    const retryDelays = [0, 600, 1500, 3000];
    let lastError;

    for (const delay of retryDelays) {
      if (delay > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, delay));
      }

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: AbortSignal.timeout(12000),
        });

        if (!response.ok) {
          const error = new Error(`Wishlist card request failed with ${response.status}`);
          error.status = response.status;
          throw error;
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const card = documentFragment.querySelector('[data-br-wishlist-card-fragment]');

        if (!card) throw new Error('Wishlist card fragment is missing');
        return card.outerHTML;
      } catch (error) {
        lastError = error;

        const status = Number(error?.status || 0);
        const isPermanentClientError = status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
        if (isPermanentClientError) break;
      }
    }

    throw lastError || new Error('Wishlist card request failed');
  }

  #createServerCard(markup, item) {
    const listItem = document.createElement('li');
    listItem.className = 'br-wishlist__item product-grid__item';
    listItem.dataset.productId = String(item.id);

    const template = document.createElement('template');
    template.innerHTML = markup.trim();
    const card = template.content.firstElementChild;

    if (!card) return this.#createFallbackCard(item);

    const productCard = card.querySelector('product-card');
    if (productCard) productCard.id = `wishlist-product-card-${item.id}`;

    const remove = card.querySelector('[data-br-wishlist-remove]');
    if (remove) {
      remove.dataset.brWishlistRemove = String(item.id);
      remove.setAttribute(
        'aria-label',
        (this.dataset.removeAriaTemplate || '').replace('{product}', item.title),
      );
    }

    listItem.append(card);
    return listItem;
  }

  #createSkeleton(item) {
    const listItem = document.createElement('li');
    listItem.className = 'br-wishlist__item br-wishlist__skeleton';
    listItem.dataset.productId = String(item.id);
    listItem.dataset.productHandle = item.handle;
    listItem.setAttribute('aria-hidden', 'true');

    const media = document.createElement('div');
    media.className = 'br-wishlist__skeleton-media';

    const title = document.createElement('div');
    title.className = 'br-wishlist__skeleton-line';

    const price = document.createElement('div');
    price.className = 'br-wishlist__skeleton-line br-wishlist__skeleton-line--short';

    const button = document.createElement('div');
    button.className = 'br-wishlist__skeleton-button';

    listItem.append(media, title, price, button);
    return listItem;
  }

  #createFallbackCard(item) {
    const listItem = document.createElement('li');
    listItem.className = 'br-wishlist__item product-grid__item';
    listItem.dataset.productId = String(item.id);

    const article = document.createElement('article');
    article.className = 'br-wishlist-card-fallback';

    const productUrl = item.url || `/products/${encodeURIComponent(item.handle)}`;
    const media = document.createElement('a');
    media.className = 'br-wishlist-card-fallback__media';
    media.href = productUrl;
    media.setAttribute('aria-label', item.title);

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 720;
      image.height = 850;
      media.append(image);
    }

    const content = document.createElement('div');
    content.className = 'br-wishlist-card-fallback__content';

    const title = document.createElement('h2');
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.textContent = item.title;
    title.append(titleLink);

    const message = document.createElement('p');
    message.textContent = this.dataset.viewLabel || 'View product';

    const remove = document.createElement('button');
    remove.className = 'br-wishlist-card__remove';
    remove.type = 'button';
    remove.dataset.brWishlistRemove = String(item.id);
    remove.title = this.dataset.removeLabel || '';
    remove.setAttribute(
      'aria-label',
      (this.dataset.removeAriaTemplate || '').replace('{product}', item.title),
    );

    content.append(title, message);
    article.append(media, remove, content);
    listItem.append(article);
    return listItem;
  }
}

if (!customElements.get('br-wishlist-button')) {
  customElements.define('br-wishlist-button', BrWishlistButton);
}

if (!customElements.get('br-wishlist-page')) {
  customElements.define('br-wishlist-page', BrWishlistPage);
}

updateWishlistLinks();
document.addEventListener('br:wishlist:change', (event) => updateWishlistLinks(event.detail?.items));
